import { z } from 'zod'

/**
 * Wire contracts for the public storefront API and the admin API.
 *
 * These live in the shared package so the theme extension, the Polaris SPA and the Worker
 * cannot drift from each other, a field renamed in one place is a typecheck failure in the
 * other two rather than a bug a merchant reports six weeks later.
 */

/**
 * Email validation before any write (SW-305). Deliberately stricter than the RFC: the RFC
 * permits addresses no mail provider will accept, and every junk row costs a fan-out read
 * forever afterwards.
 */
export const emailSchema = z
  .string()
  .trim()
  .toLowerCase()
  .min(5)
  .max(254)
  .regex(
    /^[^\s@,;:<>()[\]\\"]+@[a-z0-9]([a-z0-9-]*[a-z0-9])?(\.[a-z0-9]([a-z0-9-]*[a-z0-9])?)+$/,
    'invalid email',
  )

/** E.164 only. Anything looser and the SMS provider rejects at send time, after quota spend. */
export const phoneSchema = z
  .string()
  .trim()
  .regex(/^\+[1-9]\d{7,14}$/, 'must be E.164')

export const subscribeRequestSchema = z
  .object({
    shop: z.string().trim().toLowerCase(),
    productId: z.string().trim().min(1),
    variantId: z.string().trim().min(1),
    email: emailSchema.optional(),
    phone: phoneSchema.optional(),
    locale: z.string().trim().max(12).default('en'),
    channels: z
      .array(z.enum(['email', 'sms']))
      .min(1)
      .default(['email']),
    consentCopyVersion: z.string().trim().max(40).optional(),
    smsConsentGiven: z.boolean().default(false),
    turnstileToken: z.string().trim().max(2048).optional(),
  })
  .refine((v) => v.email !== undefined || v.phone !== undefined, {
    message: 'email or phone is required',
  })

export type SubscribeRequest = z.infer<typeof subscribeRequestSchema>

export const subscribeResponseSchema = z.object({
  /**
   * `already_subscribed` is a success, not an error. Shoppers forget they signed up, and an
   * error here reads as a broken site, the exact review competitors collect (SW-303).
   */
  status: z.enum(['subscribed', 'already_subscribed', 'pending_confirmation']),
  message: z.string(),
})

export type SubscribeResponse = z.infer<typeof subscribeResponseSchema>

export const apiErrorSchema = z.object({
  error: z.string(),
  message: z.string().optional(),
  /** Present on 429 so the widget can back off rather than hammer. */
  retryAfter: z.number().int().optional(),
})

export type ApiError = z.infer<typeof apiErrorSchema>

/** Dashboard payload (SW-602). Served entirely from precomputed rollups. */
export const dashboardSchema = z.object({
  currency: z.string(),
  range: z.object({ days: z.number().int(), from: z.string(), to: z.string() }),
  headline: z.object({
    recoveredRevenueMinor: z.number().int(),
    alertsSent: z.number().int(),
    conversions: z.number().int(),
    conversionRate: z.number(),
    waitingSubscribers: z.number().int(),
    /** Subscribers × price. The Epic 8 teaser: it plants "so what should I reorder?". */
    waitingDemandMinor: z.number().int(),
  }),
  series: z.array(
    z.object({
      day: z.string(),
      alertsSent: z.number().int(),
      subscriptionsCreated: z.number().int(),
      conversions: z.number().int(),
      recoveredRevenueMinor: z.number().int(),
    }),
  ),
  topProducts: z.array(
    z.object({
      productId: z.string(),
      variantId: z.string(),
      productTitle: z.string().nullable(),
      variantTitle: z.string().nullable(),
      imageUrl: z.string().nullable(),
      waitingSubscribers: z.number().int(),
      demandValueMinor: z.number().int(),
    }),
  ),
  /** True when the shop has never captured a subscription, drives the teaching empty state. */
  isEmpty: z.boolean(),
})

export type Dashboard = z.infer<typeof dashboardSchema>

export const waitlistQuerySchema = z.object({
  search: z.string().trim().max(200).optional(),
  status: z.enum(['active', 'pending', 'notified', 'unsubscribed', 'all']).default('active'),
  sort: z.enum(['waiting', 'recent', 'value']).default('waiting'),
  cursor: z.string().max(200).optional(),
  limit: z.coerce.number().int().min(1).max(100).default(50),
})

export type WaitlistQuery = z.infer<typeof waitlistQuerySchema>
